import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef } from 'react';
import styles from '../pages/Admin/ProductDetails/ProductDetails.module.scss';
const ImageUploader = ({ entityId, images = [], onImagesChange, uploadImage, associateImage, setPrimaryImage, deleteImage, isLoading = false, deferUpload = false, }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = React.useState(false);
    const getImageId = (image, index) => {
        const isLocalFile = typeof File !== 'undefined' && image instanceof File;
        return image.id || (isLocalFile ? `${image.name}-${image.lastModified}-${index}` : `${index}`);
    };
    const localPreviewUrls = React.useMemo(() => {
        const previewUrlMap = new Map();
        if (!deferUpload) {
            return previewUrlMap;
        }
        (images || []).forEach((image, index) => {
            const isLocalFile = typeof File !== 'undefined' && image instanceof File;
            if (!isLocalFile) {
                return;
            }
            const imageId = getImageId(image, index);
            previewUrlMap.set(imageId, URL.createObjectURL(image));
        });
        return previewUrlMap;
    }, [deferUpload, images]);
    React.useEffect(() => {
        return () => {
            localPreviewUrls.forEach((url) => {
                URL.revokeObjectURL(url);
            });
        };
    }, [localPreviewUrls]);
    const handleAddImageClick = () => {
        if (uploading || isLoading)
            return;
        fileInputRef.current?.click();
    };
    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        if (deferUpload) {
            onImagesChange([...(images || []), file]);
            if (fileInputRef.current)
                fileInputRef.current.value = '';
            return;
        }
        setUploading(true);
        try {
            const imageData = await uploadImage(file);
            const updatedEntity = await associateImage(entityId, imageData.id);
            onImagesChange(updatedEntity.images || []);
        }
        finally {
            setUploading(false);
            if (fileInputRef.current)
                fileInputRef.current.value = '';
        }
    };
    const handleSetPrimary = async (imageId) => {
        if (deferUpload) {
            const selectedImage = images.find((img) => String(img?.id) === String(imageId));
            if (!selectedImage) {
                return;
            }
            const remainingImages = images.filter((img) => String(img?.id) !== String(imageId));
            onImagesChange([selectedImage, ...remainingImages]);
            return;
        }
        const updatedEntity = await setPrimaryImage(entityId, imageId);
        onImagesChange(updatedEntity.images || []);
    };
    const handleDelete = async (imageId) => {
        if (deferUpload) {
            onImagesChange((images || []).filter((img) => String(img?.id) !== String(imageId)));
            return;
        }
        const updatedEntity = await deleteImage(entityId, imageId);
        onImagesChange(updatedEntity.images || []);
    };
    // Sort: primary first, then by sortOrder
    const sortedImages = [...images].sort((a, b) => {
        const aPrimary = a.isPrimary ?? a.is_primary ? 1 : 0;
        const bPrimary = b.isPrimary ?? b.is_primary ? 1 : 0;
        if (bPrimary !== aPrimary)
            return bPrimary - aPrimary;
        return (a.sortOrder || a.sort_order || 0) - (b.sortOrder || b.sort_order || 0);
    });
    return (_jsxs("div", { className: styles.imagesGrid, children: [sortedImages.map((image, index) => {
                const isLocalFile = typeof File !== 'undefined' && image instanceof File;
                const imageId = getImageId(image, index);
                const imageUrl = isLocalFile
                    ? localPreviewUrls.get(imageId)
                    : image.imageUrl || image.image_url;
                const isPrimary = deferUpload ? index === 0 : image.isPrimary ?? image.is_primary;
                return (_jsxs("div", { className: `${styles.imageBox} ${styles.imageWithActions}`, style: { position: 'relative', border: isPrimary ? '2px solid #007bff' : undefined }, onClick: async (e) => {
                        if (isPrimary)
                            return;
                        e.stopPropagation();
                        await handleSetPrimary(imageId);
                    }, ...(!isPrimary ? { 'aria-label': 'set as primary' } : {}), children: [imageUrl ? (_jsx("img", { src: imageUrl, alt: image.imageName || image.image_name || image.url || image.imageUrl || image.name || `Image ${index + 1}`, onError: (e) => {
                                const target = e.target;
                                if (!target.dataset.fallbackUsed) {
                                    target.dataset.fallbackUsed = 'true';
                                    target.src = '/placeholder.jpg';
                                }
                            }, style: { cursor: !isPrimary ? 'pointer' : undefined } })) : (_jsx("div", { className: styles.placeholderText, children: "No Image" })), _jsx("div", { className: styles.deleteIcon, "aria-label": "delete image", onClick: (e) => {
                                e.stopPropagation();
                                handleDelete(imageId);
                            }, children: "\u00D7" }), isPrimary && (_jsx("span", { style: {
                                position: 'absolute',
                                left: '5px',
                                bottom: '5px',
                                fontSize: '12px',
                                padding: '2px 8px',
                                background: '#28a745',
                                color: '#fff',
                                borderRadius: '3px',
                                zIndex: 2,
                            }, children: "Primary" }))] }, imageId));
            }), _jsxs("div", { className: `${styles.imageBox} ${styles.addImageBox}`, onClick: handleAddImageClick, style: {
                    opacity: uploading || isLoading ? 0.5 : 1,
                    cursor: uploading || isLoading ? 'not-allowed' : 'pointer',
                }, children: [_jsx("input", { type: "file", accept: "image/*", ref: fileInputRef, style: { display: 'none' }, onChange: handleFileChange, disabled: uploading || isLoading, "aria-label": "add image" }), _jsx("span", { className: styles.plusIcon, children: uploading || isLoading ? '...' : '+' })] })] }));
};
export default ImageUploader;
